import type { Transport } from '../types';
import { extractText, postChat, readLines } from './http';

/**
 * Newline-delimited JSON (`application/x-ndjson`): one JSON object per line.
 *
 *   {"delta":"Hel"}
 *   {"delta":"lo"}
 *   {"done":true}
 */
export const ndjsonTransport: Transport = async function* (req) {
  const response = await postChat(req, true);

  for await (const line of readLines(response)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed === '[DONE]') continue;

    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      yield { type: 'delta', text: line };
      continue;
    }

    const obj = parsed as Record<string, unknown>;
    if (obj?.done === true || obj?.type === 'done') return;
    if (obj?.error) {
      yield { type: 'error', error: String(obj.error) };
      return;
    }

    const text = extractText(parsed);
    if (text) {
      yield { type: obj?.type === 'message' ? 'message' : 'delta', text };
    } else if (parsed && typeof parsed === 'object') {
      yield { type: 'meta', data: obj };
    }
  }
};

/**
 * Raw chunked text (`text/plain`). Every byte the server writes is appended
 * verbatim, which is the least a backend can possibly implement.
 */
export const textStreamTransport: Transport = async function* (req) {
  const response = await postChat(req, true);
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Streaming is not supported in this environment');

  const decoder = new TextDecoder();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      const text = decoder.decode(value, { stream: true });
      if (text) yield { type: 'delta', text };
    }
    const tail = decoder.decode();
    if (tail) yield { type: 'delta', text: tail };
  } finally {
    reader.releaseLock?.();
  }
};
